import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import { Button, Divider, ListItem, ListItemIcon, Popover, Stack, Typography } from "@mui/material";
import deepEqual from "fast-deep-equal";
import { useState } from "react";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import type { ReportGroup } from "../../../../../../api/types/svTypes.ts";
import GlobalDndProvider from "../../../../../common/GlobalDndProvider";
import SortableList from "../../../../../common/SortableList";

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  groups: ReportGroup[];
  onApply: (groups: ReportGroup[]) => Promise<void>;
}

const getGroupId = (group: ReportGroup) => group.id;

const ReorderGroupsPopover = ({ anchorEl, onClose, groups, onApply }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const [orderedGroups, setOrderedGroups] = useState<ReportGroup[]>(groups);
  const [saving, setSaving] = useState(false);
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);

  const handleApply = async () => {
    setSaving(true);
    try {
      await onApply(orderedGroups.map((g, index) => ({ ...g, order: index })));
      onClose();
    } catch (error) {
      logError(error, "[ReorderGroupsPopover] reorderGroups");
      sendNotificationError("An error occurred while saving the group order.");
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setOrderedGroups(groups);
    onClose();
  };

  const isDirty = !deepEqual(
    orderedGroups.map((g) => g.id),
    groups.map((g) => g.id)
  );

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={handleCancel}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <Stack ref={setPanel} sx={[{ overflow: "hidden" }, (t) => ({ width: t.spacing(40), maxHeight: "80vh" })]}>
        <Stack
          direction="row"
          sx={{ px: 2.5, py: 2, justifyContent: "space-between", alignItems: "center", width: "100%" }}
        >
          <Typography variant="h6">Group Order</Typography>
          <CloseIconButton onClick={handleCancel} />
        </Stack>
        <Divider flexItem />
        <Stack sx={{ overflow: "auto", px: 1, py: 1.5 }}>
          <GlobalDndProvider>
            <SortableList
              type="report_group"
              items={orderedGroups}
              getItemId={getGroupId}
              onReorder={setOrderedGroups}
              dropArea={panel}
              renderItem={(group) => (
                <ListItem>
                  <ListItemIcon sx={{ minWidth: "unset", mr: 0.75 }}>
                    <DragIndicatorRoundedIcon />
                  </ListItemIcon>
                  <TypographyTooltipEllipsis text={group.caption} />
                </ListItem>
              )}
            />
          </GlobalDndProvider>
        </Stack>
        <Divider flexItem />
        <Stack spacing={1} direction="row" sx={{ px: 2, py: 1.5 }}>
          <Button variant="contained" onClick={handleApply} disabled={!isDirty} loading={saving}>
            Apply
          </Button>
          <Button variant="text" color="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};

export default ReorderGroupsPopover;
